import { createContext, useState, useContext, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const NavigationBlockerContext = createContext();

export const NavigationBlockerProvider = ({ children }) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const blockNavigation = useCallback((blocked) => {
    setIsBlocked(blocked);
  }, []);

  const navigateSafely = useCallback(
    (path) => {
      if (isBlocked) {
        setPendingPath(path);
        setShowDialog(true);
        return true;
      }
      navigate(path);
      return false;
    },
    [isBlocked, navigate],
  );

  const handleConfirm = () => {
    setShowDialog(false);
    setIsBlocked(false);
    toast.dismiss('simulation-loading');
    if (pendingPath) {
      navigate(pendingPath);
      setPendingPath(null);
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    setPendingPath(null);
  };

  return (
    <NavigationBlockerContext.Provider value={{ blockNavigation, navigateSafely, isBlocked }}>
      {children}
      <Dialog open={showDialog} onClose={handleCancel}>
        <DialogTitle>{t('dialog.simulationInProgressTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('dialog.simulationInProgressMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>{t('dialog.stay')}</Button>
          <Button onClick={handleConfirm} color="error">
            {t('dialog.leave')}
          </Button>
        </DialogActions>
      </Dialog>
    </NavigationBlockerContext.Provider>
  );
};

export const useNavigationBlocker = () => useContext(NavigationBlockerContext);
